// Supabase SDK error transformation helpers.
//
// NOT to be confused with utils/authErrorHandler.ts — that's a class-based
// axios 401/403 mapper for our own API responses. This module covers the
// Supabase auth-js error surface (sign-in / sign-up / resend / etc.).

// Plan 230 (#928): invite-only registration. When Supabase "Allow new users
// to sign up" is OFF, both email/password signUp and Google-OAuth new-user
// attempts fail with this signal. Show a friendly invite-only message instead
// of the raw provider text.
export const INVITE_ONLY_MESSAGE =
  "Glyphe is invite-only right now. If you've been invited, use the link in your invite email. Otherwise, contact us to request access.";

// Supabase signals disabled signups two ways: an error `code` / OAuth
// `error_code` (preferred — stable across wording changes), and a human message
// / OAuth `error_description` ("Signups not allowed for this instance"). Confirm
// the exact code on staging (plan Task 0d/6). Match the code first; normalize
// the message before comparing so a case/encoding tweak upstream doesn't slip
// through.
const SIGNUPS_DISABLED_CODES = new Set(['signup_disabled']);
const SIGNUPS_DISABLED_MESSAGE = 'signups not allowed for this instance';

export function isSignupsDisabledError(codeOrMessage: string): boolean {
  const v = codeOrMessage.trim().toLowerCase();
  return SIGNUPS_DISABLED_CODES.has(v) || v === SIGNUPS_DISABLED_MESSAGE;
}

const AUTH_ERROR_MAP: Record<string, string> = {
  'Invalid login credentials': 'Incorrect email or password. Please try again.',
  'Email not confirmed': 'Please check your email and confirm your account before signing in.',
  'User already registered': 'An account with this email already exists. Try signing in instead.',
  'Password should be at least 8 characters': 'Password must be at least 8 characters long.',
  'Email rate limit exceeded': 'Too many attempts. Please wait a few minutes and try again.',
  'For security purposes, you can only request this after 60 seconds': 'Please wait 60 seconds before trying again.',
};

export function mapAuthError(message: string): string {
  // Route any disabled-signups variant (code or message) to the invite-only
  // copy before the exact-string map.
  if (isSignupsDisabledError(message)) return INVITE_ONLY_MESSAGE;
  return AUTH_ERROR_MAP[message] || message;
}

export function extractAuthErrorCode(error: unknown, rawMessage: string): string | null {
  // Primary: typed AuthError.code from auth-js. Fallback: narrow message match
  // for 'Email not confirmed', covering older GoTrue versions that may not
  // populate .code. Only one case is covered — not a general reverse-map.
  const code = (error as { code?: string } | null | undefined)?.code;
  if (code) return code;
  if (rawMessage === 'Email not confirmed') return 'email_not_confirmed';
  return null;
}

export function isResendRateLimitError(error: unknown): boolean {
  if ((error as { code?: string } | null | undefined)?.code === 'over_email_send_rate_limit') return true;
  const message = error instanceof Error ? error.message : '';
  // Older GoTrue surfaces this verbatim without a code. Keep this string in
  // sync with AUTH_ERROR_MAP above if Supabase changes it.
  return message === 'For security purposes, you can only request this after 60 seconds';
}
